/**
 * widgets/registry.system.tsx — widgety pro toolbox Resources + System.
 * Poskládané ze šablon (statWidget/listWidget) nad system-settings/toolbox tooly.
 */
import { Activity, Clock, Cpu, HardDrive, MemoryStick, Server, SlidersHorizontal } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import i18n from '../lib/i18n';
import { listWidget, statWidget } from './templates';
import type { WidgetSpec } from './types';

const GB = 1024 ** 3;
const gb = (n: number) => (n / GB).toFixed(1);
const pct = (used: number, total: number) => (total > 0 ? Math.round((used / total) * 100) : 0);

interface SysInfo {
  hostname: string;
  os: string;
  kernel: string;
  cpu: { model: string; cores: number; load: number };
  memory: { total: number; used: number; free: number; available: number };
  disk: { total: number; used: number; free: number };
  uptime: number;
}

function fmtUptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

const REFRESH = 15_000;

export const SYSTEM_WIDGETS: WidgetSpec[] = [
  statWidget<SysInfo>({
    id: 'system-settings.cpu',
    title: 'CPU load',
    module: 'system-settings',
    tool: 'system-settings.system_info',
    icon: Cpu,
    refetchInterval: REFRESH,
    pick: (d) => ({
      value: d.cpu.load,
      unit: '%',
      sub: i18n.t('widgets.coresSub', '{{count}} jader · {{model}}', {
        count: d.cpu.cores,
        model: d.cpu.model,
      }),
    }),
  }),
  statWidget<SysInfo>({
    id: 'system-settings.ram',
    title: 'RAM',
    module: 'system-settings',
    tool: 'system-settings.system_info',
    icon: MemoryStick,
    refetchInterval: REFRESH,
    pick: (d) => ({
      value: pct(d.memory.used, d.memory.total),
      unit: '%',
      sub: `${gb(d.memory.used)} / ${gb(d.memory.total)} GB`,
    }),
  }),
  statWidget<SysInfo>({
    id: 'system-settings.disk',
    title: 'Disk',
    module: 'system-settings',
    tool: 'system-settings.system_info',
    icon: HardDrive,
    refetchInterval: REFRESH,
    pick: (d) => ({
      value: pct(d.disk.used, d.disk.total),
      unit: '%',
      sub: `${gb(d.disk.used)} / ${gb(d.disk.total)} GB`,
    }),
  }),
  statWidget<SysInfo>({
    id: 'system-settings.uptime',
    title: 'Uptime',
    module: 'system-settings',
    tool: 'system-settings.system_info',
    icon: Clock,
    refetchInterval: REFRESH,
    pick: (d) => ({ value: fmtUptime(d.uptime), sub: d.hostname }),
  }),
  statWidget<SysInfo>({
    id: 'system-settings.host',
    title: 'Systém',
    module: 'system-settings',
    tool: 'system-settings.system_info',
    icon: Server,
    pick: (d) => ({ value: d.hostname, sub: `${d.os} · ${d.kernel}` }),
  }),

  // Disk usage per mount (toolbox.disk_info → usage[])
  listWidget<{ filesystem?: string; mountpoint?: string; use_percent?: string; size?: string }>({
    id: 'toolbox.disk-usage',
    title: 'Využití disků',
    module: 'toolbox',
    tool: 'toolbox.disk_info',
    items: 'usage',
    icon: Activity,
    format: 'md',
    limit: { mobile: 4, desktop: 10 },
    emptyLabel: i18n.t('widgets.noData', 'Bez dat'),
    row: (u) => (
      <div className='flex items-center gap-2'>
        <HardDrive size={13} className='text-gray-500' />
        <span className='text-gray-200 font-mono text-xs'>{u.mountpoint || u.filesystem}</span>
        <span className='ml-auto text-xs text-gray-400'>{u.use_percent}</span>
        <span className='text-xs text-gray-600'>{u.size}</span>
      </div>
    ),
  }),

  // Per-kontejner resource profily (toolbox.resources_list → items[])
  listWidget<{ label: string; module: string; profile: string | null }>({
    id: 'toolbox.resources',
    title: 'Resource profily',
    module: 'toolbox',
    tool: 'toolbox.resources_list',
    items: 'items',
    icon: SlidersHorizontal,
    format: 'lg',
    limit: { mobile: 5, desktop: 14 },
    emptyLabel: i18n.t('dashboard.noContainers', 'Žádné kontejnery'),
    row: (r) => (
      <div className='flex items-center gap-2'>
        <span className='text-gray-200 truncate'>{r.label}</span>
        <Badge variant='gray'>{r.module}</Badge>
        <Badge variant={r.profile ? 'blue' : 'gray'} className='ml-auto'>
          {r.profile || 'default'}
        </Badge>
      </div>
    ),
  }),
];
